export interface ForwardingCondition {
  id: string
  title: string
  description: string
  condition: string
  category: "individual" | "business"
}

export const commonForwardingConditions: ForwardingCondition[] = [
  // For Individuals
  {
    id: "speak_to_human",
    title: "Speak to Human",
    description: "Forward calls when the caller wants to talk to a real person",
    condition: "When the caller asks to speak to a human, real person, or says they don't want to talk to AI",
    category: "individual"
  },
  {
    id: "family_emergency",
    title: "Family Emergency",
    description: "Forward calls when the caller mentions a family emergency or urgent personal matter",
    condition: "When the caller mentions a family emergency or urgent personal matter",
    category: "individual"
  },
  {
    id: "medical_appointment",
    title: "Medical Appointment",
    description: "Forward calls when the caller is calling about medical appointments or health matters",
    condition: "When the caller is calling about a medical appointment or health matter",
    category: "individual"
  },
  {
    id: "delivery_issue",
    title: "Delivery or Package Issue",
    description: "Forward calls when the caller has issues with deliveries or packages",
    condition: "When the caller has an issue with a delivery or package",
    category: "individual"
  },
  {
    id: "financial_matter",
    title: "Financial or Banking Matter",
    description: "Forward calls when the caller is discussing financial issues, banking, or money matters",
    condition: "When the caller is calling about financial or banking matters",
    category: "individual"
  },

  // For Businesses
  {
    id: "business_hours",
    title: "During Business Hours",
    description: "Forward calls when they mention needing immediate assistance during business hours",
    condition:
      "When the caller says they need immediate help, it's urgent, or mentions this is time-sensitive during business hours (9 AM - 5 PM)",
    category: "business"
  },
  {
    id: "emergency_service",
    title: "Emergency Service Request",
    description: "Forward urgent service calls like water leaks, power outages, or heating issues",
    condition:
      "When the caller mentions an emergency, urgent repair, water leak, power outage, heating not working, or any service issue that needs immediate attention",
    category: "business"
  },
  {
    id: "appointment_scheduling",
    title: "Appointment Scheduling",
    description: "Forward when customers want to schedule appointments or consultations",
    condition:
      "When the caller wants to schedule an appointment, book a consultation, set up a meeting, or asks about availability for services",
    category: "business"
  },
  {
    id: "billing_payment",
    title: "Billing & Payment Issues",
    description: "Forward calls related to billing disputes, payment problems, or account issues",
    condition:
      "When the caller mentions billing issues, payment problems, account disputes, wants to discuss their bill, or has questions about charges",
    category: "business"
  },
  {
    id: "new_customer",
    title: "New Customer Inquiries",
    description: "Forward calls from potential new customers asking about services",
    condition:
      "When the caller is a new customer, asking about your services, wants a quote, or is interested in learning more about what you offer",
    category: "business"
  },
  {
    id: "complaint_resolution",
    title: "Complaints & Issues",
    description: "Forward calls when customers are unhappy or need to file complaints",
    condition:
      "When the caller is upset, wants to file a complaint, mentions poor service, or needs to speak to a manager about a problem",
    category: "business"
  },
  {
    id: "technical_support",
    title: "Technical Support",
    description: "Forward calls needing technical help with products or services",
    condition:
      "When the caller needs technical support, help with equipment, troubleshooting, or assistance with how something works",
    category: "business"
  },
  {
    id: "sales_inquiries",
    title: "Sales & Pricing Questions",
    description: "Forward calls from customers asking about pricing, products, or making purchases",
    condition:
      "When the caller asks about pricing, wants to buy something, inquires about products or services, or mentions making a purchase",
    category: "business"
  },
  {
    id: "vip_customer",
    title: "VIP/Priority Customers",
    description: "Forward calls from high-value or priority customers",
    condition:
      "When the caller mentions they are a premium member, VIP customer, long-time client, or specifically asks to speak with a manager or owner",
    category: "business"
  },
  {
    id: "owner_manager_admin",
    title: "Ask for Owner/Manager/Admin",
    description:
      "Forward calls when the caller asks to speak with the owner, manager, admin, or another decision-maker",
    condition: "When the caller asks to speak with the owner, manager, admin, decision-maker, or requests a supervisor",
    category: "business"
  }
]

export const getForwardingConditionById = (id: string): ForwardingCondition | undefined => {
  return commonForwardingConditions.find((condition) => condition.id === id)
}

export const getForwardingConditionByTitle = (title: string): ForwardingCondition | undefined => {
  return commonForwardingConditions.find((condition) => condition.title === title)
}
